import * as React from 'react'

import { Option } from '@/components/ui/select'
import { EScopeLevel, ResponseRoleList } from '@/schema'

import { usePostListRoles } from '../../role-management'
import { ERoleType } from '../constants'
import { useUserManagementStore } from '../store'
import { TRole } from '../types'
import { getAvailableRoles } from '../utils'

type TUseGetRolesProps = {
  scopeLevel?: EScopeLevel | null
  enabled?: boolean
}

type TUseGetRoles = {
  roles: ResponseRoleList[]
  availableRoles: Option[]
  teamMemberRole: ResponseRoleList | undefined
  orgAdminRole: ResponseRoleList | undefined
  projectLeadRole: ResponseRoleList | undefined
  reviewerRole: ResponseRoleList | undefined
  viewerRole: ResponseRoleList | undefined
}

/**
 * Display labels for role types
 */
const ROLE_LABELS: Record<string, string> = {
  [ERoleType.ORGANIZATION_ADMIN]: 'Org Admin',
  [ERoleType.PROJECT_LEAD]: 'Project Lead',
  [ERoleType.TEAM_MEMBER]: 'Team Member',
  [ERoleType.REVIEWER]: 'Reviewer',
  [ERoleType.VIEWER]: 'Viewer',
}

export const useGetRoles = (props: TUseGetRolesProps): TUseGetRoles => {
  const { scopeLevel = EScopeLevel.ORGANIZATION, enabled = false } = props

  const currentUserRole = useUserManagementStore(
    (state) => state.currentUserRole
  )

  const [roles, setRoles] = React.useState<ResponseRoleList[]>([])

  const { mutate: postListRoles } = usePostListRoles()

  React.useEffect(() => {
    if (enabled) {
      postListRoles(
        { data: { scope_level: scopeLevel } },
        {
          onSuccess: ({ data: listRoles }) => {
            // Set roles in state
            setRoles(listRoles)
          },
        }
      )
    }
  }, [enabled, postListRoles, scopeLevel])

  // Get available roles based on inviter's role
  const availableRoles = React.useMemo(() => {
    if (scopeLevel === EScopeLevel.ORGANIZATION) {
      const roleNames = getAvailableRoles(currentUserRole as TRole)
      return roleNames
        .map((roleName) => {
          const role = roles.find((r) => r.role_name === roleName)
          // If role is not found, return null
          if (!role) return null
          return { value: role.id, label: ROLE_LABELS[role.role_name as TRole] }
        })
        .filter(Boolean) as Option[]
    }

    return roles
      .map((role) => {
        if (!ROLE_LABELS[role.role_name]) return null

        return {
          value: role.id,
          label: ROLE_LABELS[role.role_name],
        }
      })
      .filter(Boolean) as Option[]
  }, [currentUserRole, roles, scopeLevel])

  const teamMemberRole = React.useMemo(() => {
    return roles.find((r) => r.role_name === ERoleType.TEAM_MEMBER)
  }, [roles])

  const orgAdminRole = React.useMemo(() => {
    return roles.find((r) => r.role_name === ERoleType.ORGANIZATION_ADMIN)
  }, [roles])

  const projectLeadRole = React.useMemo(() => {
    return roles.find((r) => r.role_name === ERoleType.PROJECT_LEAD)
  }, [roles])

  const reviewerRole = React.useMemo(() => {
    return roles.find((r) => r.role_name === ERoleType.REVIEWER)
  }, [roles])

  const viewerRole = React.useMemo(() => {
    return roles.find((r) => r.role_name === ERoleType.VIEWER)
  }, [roles])

  return React.useMemo(
    () => ({
      roles,
      availableRoles,
      teamMemberRole,
      orgAdminRole,
      projectLeadRole,
      reviewerRole,
      viewerRole,
    }),
    [
      availableRoles,
      orgAdminRole,
      projectLeadRole,
      reviewerRole,
      roles,
      teamMemberRole,
      viewerRole,
    ]
  )
}
